import { intervalToDuration } from "date-fns";
import type { Ref } from "vue";

import type { Controller } from "@ctrl/controller-rpc";

import { formatDurationFull } from "@/components/dates";
import type { UseProfile } from "@/composables/useProfile";
import { useSoundReminder } from "@/composables/useSoundReminder";
import { conference, getDuration } from "@/event";
import { RouteNames } from "@/routes/types";
import { in_electron } from "@/utils/in";
import {
  NATIVE_NOTIFICATION_IPC,
  type NativeNotificationPayload,
} from "@/utils/native/notification/ipc";
import { playEventReminderSound } from "@/utils/sound";

export function useNativeNotification(
  controller: Controller,
  profile: Ref<UseProfile>
) {
  const { preEventSound } = useSoundReminder(profile);
  const hook = controller.notifications.upcomingEvents();
  const subscription = hook.attach(async (eventId) => {
    const event = (await controller.event.get(eventId)).release();
    if (!event) return;
    const date = new Date();
    const timeBeforeStart = getDuration(date, event.start);

    // Play the pre-event sound during the last minute before the event starts.
    if (preEventSound.value && timeBeforeStart <= 1 && timeBeforeStart > 0) {
      playEventReminderSound();
    }

    // Native notifications are only shown in Electron, and not during the last minute.
    if (!in_electron || timeBeforeStart <= 1) return;

    // Show the native notification for upcoming events emitted by the controller hook.
    const { ipcRenderer: ipc } = require("electron");
    const duration = intervalToDuration({
      start: event.start,
      end: event.end,
    });
    const formattedDuration = formatDurationFull(duration);
    const meeting = conference(event);

    const payload: NativeNotificationPayload = {
      title: `${event.title} · ${formattedDuration}`,
      body: `Starting in ${Math.ceil(timeBeforeStart)} minutes...`,
      icon: meeting ? `src/assets/notification-${meeting.icon}.png` : null,
      route: { name: RouteNames.Event, params: { id: eventId } },
    };
    ipc.send(NATIVE_NOTIFICATION_IPC, payload);
  });

  return { hook, subscription };
}
